import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { LoggedInUserFragmentFragment, useVerifyLoggedInMutation } from 'data/__generated__';
import { hasAccessToken } from 'data/connection/storage';
import { useSentryUser } from 'hooks/useSentryUser';

type AuthContextType = {
    user: LoggedInUserFragmentFragment | null;
    loading: boolean;
    setUser: (user: LoggedInUserFragmentFragment | null) => void;
};

const AuthContext = createContext<AuthContextType>({
    user: null,
    loading: true,
    setUser: () => null,
});

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<LoggedInUserFragmentFragment | null>(null);
    const [loading, setLoading] = useState(true);
    const [verifyLoggedIn] = useVerifyLoggedInMutation();

    useSentryUser(user);

    useEffect(() => {
        const checkAuth = async () => {
            if (!hasAccessToken()) {
                setLoading(false);
                return;
            }

            const { data } = await verifyLoggedIn();
            const success = data?.verifyLoggedIn?.success;
            const verifiedUser = data?.verifyLoggedIn?.user;
            if (success && verifiedUser) {
                setUser(verifiedUser);
            }

            setLoading(false);
        };

        checkAuth();
    }, [verifyLoggedIn]);

    return <AuthContext.Provider value={{ user, loading, setUser }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
    return useContext(AuthContext);
}
